/**
 * OperatorPage — 運営画面 (/operator)
 *
 * キーボードショートカット:
 *   1        → 英語 → 日本語
 *   2        → 日本語 → 英語
 *   Space    → 翻訳開始 / 一時停止 / 再開
 *   Escape   → 翻訳停止
 *   C / Del  → 字幕クリア
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { useSessionStore, transcriptToText } from '../stores/sessionStore';
import { useEventStore } from '../stores/eventStore';
import { generateBookmarkUrl } from './SetupPage';
import { useAudioCapture } from '../hooks/useAudioCapture';
import { useWorkerWS } from '../hooks/useWorkerWS';
import { useSessionToken, clearSavedTestToken } from '../hooks/useSessionToken';
// Note: named export を使用
import type { ServerMessage } from '../types';

import MicSelector from '../components/MicSelector';
import AudioMeter from '../components/AudioMeter';
import TargetSelect, { type NoiseMode } from '../components/TargetSelect';
import TranscriptHistory from '../components/TranscriptHistory';
import GlossaryEditor from '../components/GlossaryEditor';
import { t, trServer, LangSwitch, useLang, getLang } from '../i18n';

// Worker URL（環境変数から取得）
const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? 'http://localhost:8787';

// URLパラメータからmodeを取得
function getMode(): 'test' | 'production' {
  const p = new URLSearchParams(window.location.search);
  // checkout_session_id がある場合は必ず production
  if (p.get('checkout_session_id')) return 'production';
  return p.get('mode') === 'production' ? 'production' : 'test';
}

// URLパラメータから契約時間（分）を取得
function getContractedMinutes(): number {
  const p = new URLSearchParams(window.location.search);
  const m = parseInt(p.get('minutes') ?? '60', 10);
  return isNaN(m) || m <= 0 ? 60 : Math.min(m, 480);
}

// 管理者モードかどうか
function isAdminMode(): boolean {
  return new URLSearchParams(window.location.search).get('admin') === '1';
}

// 管理者パスワードは公開プログラムに含めない。セットアップ画面で入力され、このタブの間だけ保持する。
function getAdminSecret(): string | null {
  try { return sessionStorage.getItem('amo_admin_secret'); } catch { return null; }
}

// Stripe Checkout セッションIDを取得
// URLパラメータ → sessionStorage（PaymentSuccessPage経由）の順で探す
function getCheckoutSessionId(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get('checkout_session_id');
  if (fromUrl) return fromUrl;
  const fromPayment = new URLSearchParams(window.location.search).get('from') === 'payment';
  if (fromPayment) return sessionStorage.getItem('andiamo_checkout_session_id');
  return null;
}

// Zoom字幕API連携の表示（非表示中）
const SHOW_ZOOM_CC = false;

// Zoom字幕APIの言語コード（例: en-US）
const ZOOM_LANG: Record<string, string> = {
  ja: 'ja-JP', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', zh: 'zh-CN', ko: 'ko-KR',
  it: 'it-IT', pt: 'pt-PT', th: 'th-TH', vi: 'vi-VN', id: 'id-ID', tl: 'tl-PH',
};

export default function OperatorPage() {
  useLang();
  const store = useSessionStore();
  const { getActiveEvent, upsertEvent } = useEventStore();
  const [isStarted, setIsStarted] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [micWarning, setMicWarning] = useState<string | null>(null);
  const [bookmarkUrl, setBookmarkUrl] = useState<string | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);
  // Zoom字幕API
  const [zoomCaptionUrl, setZoomCaptionUrl] = useState('');
  const [zoomCaptionEnabled, setZoomCaptionEnabled] = useState(false);
  const zoomSeqRef = useRef(1);
  const zoomRef = useRef({ enabled: false, url: '' });
  // 2言語モード：話している言語（原文から判定）と同じ言語の「言い直し」は記録しない
  const spokenRef = useRef<string | null>(null);
  const lastSrcScriptRef = useRef<string | null>(null);
  const recentScriptsRef = useRef<string[]>([]);
  const scriptOfText = (s: string) => (/[\uAC00-\uD7AF]/.test(s) ? 'ko' : /[\u3040-\u30FF\u4E00-\u9FFF]/.test(s) ? 'cjk' : /[\u0E00-\u0E7F]/.test(s) ? 'th' : 'latin');
  const scriptOfLang = (c?: string) => (c === 'ja' || c === 'zh' ? 'cjk' : c === 'ko' ? 'ko' : c === 'th' ? 'th' : 'latin');
  const secondRef = useRef('');
  const recordTranslation = (text: string, tgt: string | undefined, direction: 'ja-en' | 'en-ja', ts?: number) => {
    const add = () => useSessionStore.getState().addTranscriptEntry({
      timestamp: ts ?? Date.now(), direction, target: tgt, original: '', translation: text, sessionName: useSessionStore.getState().sessionName,
    });
    if (!secondRef.current) { add(); return; }
    // 話している言語：直前の原文（途中経過を含む）1件で判定。なければ多数決の結果
    const spoken = () => lastSrcScriptRef.current || spokenRef.current;
    // 原文の判定が少し遅れて届くので、1.5秒待ってから判断する
    setTimeout(() => {
      const sp = spoken();
      if (sp && scriptOfLang(tgt) === sp) return;
      add();
    }, 1500);
  };
  zoomRef.current = { enabled: zoomCaptionEnabled, url: zoomCaptionUrl };
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionMode = getMode(); // 'test' | 'production'
  const billingStartedRef = useRef(false);

  // JWT認証（URLのmodeに合わせてtokenを取得）
  const { sessionToken, sessionId, expiresAt: tokenExpiresAt, testMinutes, fetchToken, verifyCheckout, startTranslation, error: tokenError, loading: tokenLoading } =
    useSessionToken();

  // 残り時間管理（本番モード）
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [remainingSec, setRemainingSec] = useState<number | null>(null);
  const [timeExpired, setTimeExpired] = useState(false);

  // 延長機能
  const [extensionLoading, setExtensionLoading] = useState(false);
  const [extensionCheckoutOpened, setExtensionCheckoutOpened] = useState(false);
  const [extensionPollTimer, setExtensionPollTimer] = useState<ReturnType<typeof setInterval> | null>(null);
  const [prodSessionId, setProdSessionId] = useState<string | null>(null); // production_sessions.id
  // 延長ボタンの金額（最初の購入の通貨に合わせる。価格は Worker の /pricing から）
  const [extPriceLabel, setExtPriceLabel] = useState<string>('¥5,500');

  // 警告バナー（DOからのtime_warning / grace_entered）
  const [timeWarning, setTimeWarning] = useState<string | null>(null);
  const [graceMode, setGraceMode] = useState(false); // grace期間中
  // 訳す先の言語・マイク環境（このブラウザに記憶）
  const [second, setSecondState] = useState<string>(() => {
    // 2つ目の言語が1つ目と同じ場合は「なし」として扱う（同じ言語を二重に翻訳しない）
    try {
      const sec = localStorage.getItem('amo_second') || '';
      return sec && sec === localStorage.getItem('amo_target') ? '' : sec;
    } catch { return ''; }
  });
  secondRef.current = second;
  const [target, setTarget] = useState<string>(() => {
    try { return localStorage.getItem('amo_target') || 'ja'; } catch { return 'ja'; }
  });
  const [noise, setNoise] = useState<NoiseMode>(() => {
    try { return (localStorage.getItem('amo_noise') as NoiseMode) || ''; } catch { return ''; }
  });

  // 翻訳履歴: デバウンス保存（interim:falseが来なくてもタイムアウトで保存）
  const captionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedCaptionRef = useRef<string>('');
  const currentCaptionRef = useRef<{ text: string; original: string; direction: string; target?: string; timestamp: number }>({ text: '', original: '', direction: 'ja-en', timestamp: 0 });
  const transcriptRef = useRef(store.transcript);
  const handleStopRef = useRef<() => void>(() => {});
  // 記録中の原文（文の終わり・言語の切り替え・20秒で次の1件へ）
  const srcEntryRef = useRef<{ id: string; at: number; script: string; text: string } | null>(null);
  // 残り時間が0になったときに、最新の「翻訳中かどうか」を見るため
  const isStartedRef = useRef(false);

  // transcriptRefを常に最新に同期
  useEffect(() => { transcriptRef.current = store.transcript; }, [store.transcript]);

  // マウント時: JWT取得 + イベントの用語集をロード
  useEffect(() => {
    const event = getActiveEvent();
    if (event) store.initFromEvent(event.glossary);

    const checkoutSessionId = getCheckoutSessionId();

    if (checkoutSessionId) {
      // 本番決済完了後: JWTのみ取得（タイマーは翻訳開始ボタン押下時に起動）
      verifyCheckout(checkoutSessionId).then((result) => {
        if (result?.sessionId) setProdSessionId(result.sessionId);
        // active セッション（再接続）の場合は expiresAt が返ってくる
        if (result?.expiresAt) setEndsAt(result.expiresAt);
      });
    } else if (sessionMode === 'production' && event?.managementToken) {
      // 購入済みイベントを別のタブ・後日に開き直した場合：決済IDが手元になくても、
      // 管理トークンで購入済みセッションを再開する（以前はここでエラーになっていた）
      startTranslation(event.id, event.managementToken, event.name ?? null).then((result) => {
        if (result?.sessionId) setProdSessionId(result.sessionId);
        if (result?.expiresAt) setEndsAt(result.expiresAt);
      });
    } else {
      fetchToken({
        mode: sessionMode,
        contractedMinutes: getContractedMinutes(),
        eventName: event?.name ?? null,
        // テスト・管理者モードでもイベントを渡す（イベント固定の字幕URL・QRのため）
        eventId: event?.id ?? null,
        ...(isAdminMode() && getAdminSecret() ? { adminSecret: getAdminSecret() ?? undefined } : {}),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 残り時間カウントダウン（テスト・本番モード共通）
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const rem = Math.max(0, endsAt - Math.floor(Date.now() / 1000));
      setRemainingSec(rem);
      if (rem <= 0) {
        setTimeExpired(true);
        if (isStartedRef.current) handleStopRef.current();
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt]);

  // 用語集が変わったらアクティブイベントに自動保存
  useEffect(() => {
    const event = getActiveEvent();
    if (event) {
      upsertEvent({ ...event, glossary: store.glossaryTerms });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.glossaryTerms]);

  // ──────────────────────────────────────────
  // WebSocket メッセージハンドラ
  // ──────────────────────────────────────────
  const handleMessage = useCallback(
    (msg: ServerMessage) => {
      switch (msg.type) {
        case 'status':
          if (msg.connected) {
            store.setConnectionStatus('connected');
          } else if (msg.connecting) {
            store.setConnectionStatus('connecting');
          } else {
            store.setConnectionStatus('disconnected');
          }
          if (msg.direction) store.setDirection(msg.direction);
          if (msg.paused !== undefined) store.setPaused(msg.paused);
          // DOのendsAtでタイマーを同期（再接続時・D1更新後リロード時）
          if (msg.endsAt && sessionMode === 'production') {
            setEndsAt(msg.endsAt as number);
          }
          if (sessionMode === 'test' && typeof msg.testEndsAt === 'number') setEndsAt(msg.testEndsAt);
          break;

        case 'caption': {
          if (msg.lane === 3) {
            // 原文（文字起こし）：履歴にだけ残す
            const t3 = msg.translation?.trim() ?? '';
            { const l = t3.replace(/[\s\p{P}]/gu, ''); if (l.length >= 3) lastSrcScriptRef.current = scriptOfText(l.slice(-12)); }
            if (!msg.interim && t3.replace(/[\s\p{P}]/gu, '').length >= 3) {
              // 話している言語を、直近3つの原文の多数決で判定（記録から「言い直し」を除くため）
              const hist = [...recentScriptsRef.current, scriptOfText(t3)].slice(-3);
              recentScriptsRef.current = hist;
              const cnt = (k: string) => hist.filter((x) => x === k).length;
              spokenRef.current = hist.reduce((b, k) => (cnt(k) > cnt(b) ? k : b), hist[hist.length - 1]);
            }
            if (!msg.interim && t3) {
              const now = Date.now();
              const sc = scriptOfText(t3);
              const cur = srcEntryRef.current;
              const ended = cur ? /[.?!。？！]\s*$/.test(cur.text) : true;
              const exists = cur ? useSessionStore.getState().transcript.some((e) => e.id === cur.id) : false;
              if (cur && exists && !ended && cur.script === sc && now - cur.at < 20000) {
                const add = (sc === 'latin' ? ' ' : '') + t3;
                store.appendTranscriptText(cur.id, add);
                cur.text += add;
              } else {
                store.addTranscriptEntry({
                  timestamp: msg.timestamp ?? now,
                  direction: msg.direction as 'ja-en' | 'en-ja',
                  target: 'src',
                  original: '',
                  translation: t3,
                  sessionName: store.sessionName,
                });
                const last = useSessionStore.getState().transcript.slice(-1)[0];
                srcEntryRef.current = last ? { id: last.id, at: now, script: sc, text: t3 } : null;
              }
            }
            break;
          }
          if (msg.lane === 2) {
            store.setCurrentCaption2({ interim: msg.interim, original: '', translation: msg.translation, direction: msg.direction, target: msg.target });
            const t2 = msg.translation?.trim() ?? '';
            if (!msg.interim && t2) recordTranslation(t2, msg.target, msg.direction as 'ja-en' | 'en-ja', msg.timestamp);
            break;
          }
          store.setCurrentCaption({
            interim: msg.interim,
            original: msg.original,
            translation: msg.translation,
            direction: msg.direction,
            target: msg.target,
          });

          const captionText = msg.translation?.trim() ?? '';
          const captionOriginal = msg.original?.trim() ?? '';

          // 最新のキャプション情報をrefに保持（停止時の保存に使用）
          if (captionText) {
            currentCaptionRef.current = {
              text: captionText,
              original: captionOriginal,
              direction: msg.direction,
              target: msg.target,
              timestamp: msg.timestamp ?? Date.now(),
            };
          }

          // interim: false（確定字幕）のみ履歴に保存
          if (!msg.interim && captionText) {
            if (captionText !== lastSavedCaptionRef.current) {
              lastSavedCaptionRef.current = captionText;
              if (secondRef.current) {
                recordTranslation(captionText, msg.target, msg.direction as 'ja-en' | 'en-ja', msg.timestamp);
              } else {
                store.addTranscriptEntry({
                  timestamp: msg.timestamp ?? Date.now(),
                  direction: msg.direction as 'ja-en' | 'en-ja',
                  target: msg.target,
                  original: captionOriginal,
                  translation: captionText,
                  sessionName: store.sessionName,
                });
              }
            }
          }

          // 確定字幕のZoom字幕API送信
          if (!msg.interim && captionText) {
            // Zoom字幕API送信
            const zc = zoomRef.current;
            if (zc.enabled && zc.url) {
              // seq は同じURLで増え続ける必要があるため、再読み込みしても続きから使う
              const seqKey = `amo_zoom_seq:${zc.url}`;
              let seq = zoomSeqRef.current;
              try { seq = Math.max(seq, Number(localStorage.getItem(seqKey) ?? 0) + 1); } catch { /* ignore */ }
              zoomSeqRef.current = seq + 1;
              try { localStorage.setItem(seqKey, String(seq)); } catch { /* ignore */ }
              const code = msg.target ?? (msg.direction === 'ja-en' ? 'en' : 'ja');
              const zoomLang = ZOOM_LANG[code] ?? code;
              fetch(`${zc.url.trim()}&seq=${seq}&lang=${zoomLang}`, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain' },
                body: captionText,
              }).catch(() => { /* Zoom API失敗は無視 */ });
            }
          }
          break;
        }

        case 'direction':
          store.setDirection(msg.value);
          break;

        case 'clear':
          store.clearCaption();
          break;

        case 'paused':
          store.setPaused(true);
          break;

        case 'resumed':
          store.setPaused(false);
          break;

        case 'reconnecting':
          store.setConnectionStatus('reconnecting');
          store.setReconnectInfo({ attempt: msg.attempt, delay: msg.delay });
          break;

        case 'error':
          store.setConnectionStatus('error', msg.message);
          break;

        case 'speech_start':
          store.setIsSpeaking(true);
          break;

        case 'speech_stop':
          store.setIsSpeaking(false);
          break;

        case 'stopped':
          setIsStarted(false);
          store.setConnectionStatus('disconnected');
          break;

        // 時間警告（Operator のみ受信）
        case 'time_warning':
          setTimeWarning(msg.message as string ?? t(`あと${msg.minsLeft}分です`, `${msg.minsLeft} minutes left`));
          break;

        case 'grace_entered':
          setGraceMode(true);
          setTimeWarning(msg.message as string ?? '猶予に入りました。あと15分利用できます。');
          break;

        case 'extend_applied': {
          // 延長確認: タイマーを更新
          const newExpiresAt = typeof msg.expiresAt === 'number' ? msg.expiresAt : null;
          if (newExpiresAt) {
            setEndsAt(newExpiresAt);
            setTimeExpired(false);
            setGraceMode(false);
            setTimeWarning(null);
          }
          // 延長ポーリングを停止
          if (extensionPollTimer) {
            clearInterval(extensionPollTimer);
            setExtensionPollTimer(null);
          }
          setExtensionCheckoutOpened(false);
          break;
        }

        case 'test_timer':
          if (sessionMode === 'test') setEndsAt(msg.endsAt);
          break;

        case 'time_expired':
          setTimeExpired(true);
          handleStopRef.current();
          break;

        case 'debug_event':
          // デバッグ: 未処理OpenAIイベントをコンソールに出力
          console.log('[Andiamo DEBUG] OpenAI event:', msg.eventType);
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store, extensionPollTimer, endsAt]
  );

  // ──────────────────────────────────────────
  // WebSocket フック
  // ──────────────────────────────────────────
  const ws = useWorkerWS({
    url: WORKER_URL,
    role: 'operator',
    token: sessionToken ?? undefined,   // JWTをtokenとして渡す
    sessionId: sessionId ?? 'pending',  // sessionIdはJWTのsessionIdを使用
    onMessage: handleMessage,
    onOpen: () => store.setConnectionStatus('connected'),
    onClose: () => {
      if (store.connectionStatus !== 'reconnecting') {
        store.setConnectionStatus('disconnected');
      }
    },
    autoConnect: false,  // JWT取得後に手動でconnect()を呼ぶ
  });

  // JWT取得完了後にWS接続 + カウントダウン開始
  useEffect(() => {
    if (sessionToken) {
      ws.connect();
      if (sessionMode === 'test') {
        // テストモード: 「翻訳開始」を押すまでは時間を減らさない。サーバーから終了時刻（test_timer）が届いたら数え始める
        setRemainingSec((testMinutes ?? 15) * 60);
      }
      // 本番モード: endsAt は翻訳開始ボタン押下時 or DO status メッセージで設定
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  // 本番モード: WS接続後にbilling_startを送信（1回のみ）
  useEffect(() => {
    if (
      sessionMode === 'production' &&
      !billingStartedRef.current &&
      store.connectionStatus === 'connected'
    ) {
      // WSに直接送信できないのでhandleStartから送る
      // （WSの送信は翻訳開始後に行う）
      billingStartedRef.current = true;
    }
  }, [store.connectionStatus, sessionMode]);

  // ──────────────────────────────────────────
  // 音声キャプチャ フック
  // ──────────────────────────────────────────
  const audio = useAudioCapture({
    onAudioData: (base64) => ws.sendAudio(base64),
    onError: (err) => setMicWarning(err.message),
    onDeviceDisconnected: () => {
      setMicWarning(t('⚠️ マイクが切断されました。再接続してください。', '⚠️ The microphone was disconnected. Please reconnect it.'));
    },
  });

  // 初回マイク一覧取得
  useEffect(() => {
    audio.enumerateDevices();
  }, []);

  // ──────────────────────────────────────────
  // 翻訳開始
  // ──────────────────────────────────────────
  const handleStart = useCallback(async () => {
    setMicWarning(null);

    // 本番モード: タイマー未開始なら activate-timer を呼んでカウントダウン開始
    if (sessionMode === 'production' && !endsAt) {
      const event = getActiveEvent();
      if (event?.managementToken) {
        try {
          const res = await fetch(`${WORKER_URL}/event/activate-timer`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${event.managementToken}`,
            },
            body: JSON.stringify({ eventId: event.id }),
          });
          const data = await res.json() as { expiresAt?: number; hardExpiresAt?: number; error?: string };
          if (res.ok && data.expiresAt) {
            setEndsAt(data.expiresAt);
            // 時間を反映した接続につなぎ直す。この後に送る「開始」などは、つながり次第送られる
            ws.connect();
          }
        } catch (e) {
          console.error('[Andiamo] activate-timer error:', e);
        }
      }
    }

    await audio.startCapture();
    // 画面に表示中の用語集を、開始のたびにサーバーへ送る（反映ボタンの押し忘れ対策）
    ws.sendGlossary(useSessionStore.getState().glossaryTerms);
    ws.send({ type: 'target', value: target });
    ws.send({ type: 'noise', value: noise });
    ws.send({ type: 'second', value: second || null });
    ws.sendStart();
    setIsStarted(true);
    store.setStartTime(Date.now());
    store.setConnectionStatus('connecting');

    // 経過時間タイマー
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsedSec((s) => s + 1);
    }, 1000);
  }, [audio, ws, store, sessionMode, endsAt, getActiveEvent, target, noise]);

  // ──────────────────────────────────────────
  // 翻訳停止
  // ──────────────────────────────────────────
  const handleStop = useCallback(() => {
    audio.stopCapture();
    ws.sendStop();
    setIsStarted(false);
    store.setStartTime(null);

    // 停止時：サーバーが最後の字幕を確定して送るのを待ち、それでも届かなかった場合だけ保存する
    setTimeout(() => {
      const finalCaption = currentCaptionRef.current;
      if (finalCaption.text && finalCaption.text !== lastSavedCaptionRef.current) {
        lastSavedCaptionRef.current = finalCaption.text;
        store.addTranscriptEntry({
          timestamp: finalCaption.timestamp,
          direction: finalCaption.direction as 'ja-en' | 'en-ja',
          target: finalCaption.target,
          original: finalCaption.original,
          translation: finalCaption.text,
          sessionName: store.sessionName,
        });
      }
      currentCaptionRef.current = { text: '', original: '', direction: finalCaption.direction, timestamp: 0 };
    }, 3000);

    store.clearCaption();

    // 翻訳履歴は自動では保存しない（「翻訳履歴」のTXT/CSV/JSONで保存する。未保存のまま閉じるときは確認を出す）
    isStartedRef.current = false;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setElapsedSec(0);

    // 停止時に最新の用語集でブックマークURLを生成して表示
    const event = getActiveEvent();
    if (event) {
      const url = generateBookmarkUrl(event.name, store.glossaryTerms);
      setBookmarkUrl(url);
    }
  }, [audio, ws, store, getActiveEvent]);

  // handleStopRefを常に最新のhandleStopに同期
  useEffect(() => { handleStopRef.current = handleStop; }, [handleStop]);
  isStartedRef.current = isStarted;

  // 保存していない翻訳履歴があるときは、再読み込み・タブを閉じる前にブラウザの確認を出す
  useEffect(() => {
    if (!store.transcriptDirty || store.transcript.length === 0) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [store.transcriptDirty, store.transcript.length]);

  useEffect(() => {
    const ev = getActiveEvent();
    if (!prodSessionId || !ev) return;
    Promise.all([
      fetch(`${WORKER_URL}/pricing`).then((r) => r.json()),
      fetch(`${WORKER_URL}/event/payment-status?event_id=${ev.id}`).then((r) => r.json()),
    ]).then(([p, st]: [{ jp?: { hourlyPrice: number }; intl?: { hourlyPrice: number } }, { currency?: string }]) => {
      if (st?.currency === 'usd' && p?.intl) setExtPriceLabel(`$${p.intl.hourlyPrice}`);
      else if (p?.jp) setExtPriceLabel(`¥${p.jp.hourlyPrice.toLocaleString()}`);
    }).catch(() => { /* 既定のまま */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodSessionId]);

  // ──────────────────────────────────────────
  // 延長購入
  // ──────────────────────────────────────────
  const handleExtension = useCallback(async () => {
    const event = getActiveEvent();
    if (!event?.managementToken || !prodSessionId) return;

    setExtensionLoading(true);
    try {
      const res = await fetch(`${WORKER_URL}/checkout/create-extension`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${event.managementToken}`,
        },
        body: JSON.stringify({ eventId: event.id, sessionId: prodSessionId, lang: getLang() }),
      });
      const data = await res.json() as { checkoutUrl?: string; error?: string };
      if (!res.ok || !data.checkoutUrl) {
        throw new Error(data.error ?? t('延長チェックアウトの作成に失敗しました', 'Could not open the extension checkout'));
      }

      // 新しいタブで延長決済を開く（WS接続は切断しない）
      window.open(data.checkoutUrl, '_blank', 'noopener,noreferrer');
      setExtensionCheckoutOpened(true);

      // 延長完了をポーリングで検出（30秒ごと × 最大20回）
      const event2 = getActiveEvent();
      if (!event2) return;
      let pollCount = 0;
      const timer = setInterval(async () => {
        pollCount++;
        if (pollCount > 20) {
          clearInterval(timer);
          setExtensionPollTimer(null);
          return;
        }
        try {
          const statusRes = await fetch(`${WORKER_URL}/event/payment-status?event_id=${event2.id}`);
          const status = await statusRes.json() as { status?: string; expiresAt?: number; hardExpiresAt?: number; sessionId?: string };
          if (status.status === 'active' && status.expiresAt && status.expiresAt > (endsAt ?? 0)) {
            // 延長確認 → DO に通知
            ws.send({
              type: 'extend_confirmed',
              expiresAt: status.expiresAt,
              hardExpiresAt: status.hardExpiresAt,
            });
            clearInterval(timer);
            setExtensionPollTimer(null);
          }
        } catch { /* ignore */ }
      }, 30_000);
      setExtensionPollTimer(timer);
    } catch (e) {
      console.error('[Andiamo] Extension checkout error:', e);
    } finally {
      setExtensionLoading(false);
    }
  }, [getActiveEvent, prodSessionId, ws, endsAt]);

  // クリーンアップ: 延長ポーリングタイマー
  useEffect(() => {
    return () => {
      if (extensionPollTimer) clearInterval(extensionPollTimer);
    };
  }, [extensionPollTimer]);

  // ──────────────────────────────────────────
  // 一時停止 / 再開
  // ──────────────────────────────────────────
  const handleTogglePause = useCallback(() => {
    if (store.paused) {
      ws.sendGlossary(useSessionStore.getState().glossaryTerms);
      ws.sendResume();
    } else {
      ws.sendPause();
    }
  }, [store.paused, ws]);

  // ──────────────────────────────────────────
  // 翻訳方向切り替え
  // ──────────────────────────────────────────
  const handleTargetChange = useCallback((code: string) => {
    setTarget(code);
    try { localStorage.setItem('amo_target', code); } catch { /* ignore */ }
    ws.send({ type: 'target', value: code });
    // 1つ目の言語を2つ目と同じにしたら、2つ目は「なし」に戻す（画面の表示と実際の動作を一致させる）
    if (secondRef.current && secondRef.current === code) {
      setSecondState('');
      try { localStorage.setItem('amo_second', ''); } catch { /* ignore */ }
      ws.send({ type: 'second', value: null });
      store.setCurrentCaption2(null);
    }
    store.clearCaption();
  }, [ws, store]);
  const handleSecondChange = useCallback((code: string) => {
    setSecondState(code);
    try { localStorage.setItem('amo_second', code); } catch { /* ignore */ }
    ws.send({ type: 'second', value: code || null });
    store.setCurrentCaption2(null);
  }, [ws, store]);
  const handleNoiseChange = useCallback((mode: NoiseMode) => {
    setNoise(mode);
    try { localStorage.setItem('amo_noise', mode); } catch { /* ignore */ }
    ws.send({ type: 'noise', value: mode });
  }, [ws]);
  // 接続（再接続）したら、現在の設定をサーバーへ送る
  useEffect(() => {
    if (store.connectionStatus === 'connected') {
      ws.send({ type: 'target', value: target });
      ws.send({ type: 'noise', value: noise });
      ws.send({ type: 'second', value: second || null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.connectionStatus]);


  // ──────────────────────────────────────────
  // 字幕クリア
  // ──────────────────────────────────────────
  const handleClear = useCallback(() => {
    ws.sendClear();
    store.clearCaption();
  }, [ws, store]);

  // ──────────────────────────────────────────
  // キーボードショートカット
  // ──────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // input/textarea にフォーカスがある場合は無効
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }

      switch (e.key) {
        case '1':
          e.preventDefault();
          handleTargetChange('ja');
          break;
        case '2':
          e.preventDefault();
          handleTargetChange('en');
          break;
        case ' ':
          e.preventDefault();
          if (!isStarted) {
            handleStart();
          } else {
            handleTogglePause();
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (isStarted) handleStop();
          break;
        case 'c':
        case 'C':
        case 'Delete':
          e.preventDefault();
          handleClear();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStarted, handleStart, handleStop, handleTogglePause, handleTargetChange, handleClear]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      audio.stopCapture();
    };
  }, []);

  // ──────────────────────────────────────────
  // 経過時間フォーマット
  // ──────────────────────────────────────────
  const formatElapsed = (sec: number) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // ──────────────────────────────────────────
  // 接続状態表示
  // ──────────────────────────────────────────
  const statusConfig: Record<string, { label: string; cls: string }> = {
    idle: { label: t('未接続', 'Not connected'), cls: 'status-disconnected' },
    connecting: { label: t('接続中...', 'Connecting...'), cls: 'status-connecting' },
    connected: { label: t('接続済み', 'Connected'), cls: 'status-connected' },
    disconnected: { label: t('切断', 'Disconnected'), cls: 'status-disconnected' },
    error: { label: t('エラー', 'Error'), cls: 'status-error' },
    reconnecting: { label: t('再接続中...', 'Reconnecting...'), cls: 'status-reconnecting' },
  };

  // 一時停止中は接続状態より優先して表示
  const effectiveStatus = isStarted && store.paused
    ? { label: t('一時停止中', 'Paused'), cls: 'status-connecting' }
    : (statusConfig[store.connectionStatus] ?? statusConfig.idle);
  const sc = effectiveStatus;

  // caption画面はsessionIdで同じDOに接続（operatorトークンは渡さない）
  // イベント固定の字幕URL（入り直してもQRが変わらない）。取得できない場合は従来のセッション単位
  const [livePub, setLivePub] = useState<string | null>(null);
  useEffect(() => {
    if (!sessionToken) return;
    fetch(`${WORKER_URL}/live/pub?token=${encodeURIComponent(sessionToken)}`)
      .then((r) => r.json()).then((d: { pub?: string | null }) => setLivePub(d.pub ?? null)).catch(() => {});
  }, [sessionToken]);
  const captionPageUrl = livePub
    ? `${window.location.origin}/caption?event=${livePub}&style=${store.obsStyle}&bg=black`
    : `${window.location.origin}/caption?session=${sessionId ?? 'default'}&style=${store.obsStyle}&bg=black`;

  // JWT取得中 / エラー表示
  if (tokenLoading) {
    return (
      <div className="operator-layout" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ color: '#9ca3af', fontSize: '1rem' }}>{t('🔑 認証中...', '🔑 Authenticating...')}</div>
      </div>
    );
  }
  if (tokenError) {
    return (
      <div className="operator-layout" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div>
          <div className="alert alert-error">⚠️ {trServer(tokenError)}</div>
          <button className="btn btn-primary" onClick={() => fetchToken({ mode: 'test' })}>{t('再試行', 'Retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="operator-layout">
      {/* ── ヘッダー ── */}
      <header className="operator-header">
        <h1>🌐 Amo Live Translator</h1>
          {/* modeバッジ */}
          {sessionMode === 'production'
            ? <span style={{ background: '#dc2626', color: 'white', fontSize: '0.75rem', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold' }}>{t('● 本番', '● Live')}</span>
            : <span style={{ background: '#374151', color: '#9ca3af', fontSize: '0.75rem', padding: '2px 8px', borderRadius: 4 }}>{t('テスト', 'Test')}</span>
          }

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {/* 接続状態 */}
          <span className={`status-badge ${sc.cls}`}>
            <span className="status-dot" />
            {sc.label}
          </span>

          {/* 経過時間 */}
          {isStarted && (
            <span className="elapsed-timer">{formatElapsed(elapsedSec)}</span>
          )}

          {/* 残り時間（テスト・本番共通） */}
          {remainingSec !== null && !timeExpired && (
            <span style={{
              background: remainingSec < 300 ? '#7f1d1d' : graceMode ? '#78350f' : sessionMode === 'test' ? '#1e3a5f' : '#064e3b',
              color: remainingSec < 300 ? '#fca5a5' : graceMode ? '#fde68a' : sessionMode === 'test' ? '#93c5fd' : '#6ee7b7',
              fontSize: '0.75rem',
              padding: '2px 8px',
              borderRadius: 4,
              fontWeight: 'bold',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {graceMode ? t('猶予 ', 'Grace ') : t('残り ', 'Left ')}{formatElapsed(remainingSec)}
            </span>
          )}

          {/* 字幕画面リンク */}
          <a
            href={captionPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-sm"
            style={{ color: 'white', borderColor: '#4b5563' }}
          >
            {t('🖥 字幕画面を開く', '🖥 Open caption screen')}
          </a>

          {/* 会議用：2つの言語の訳を1段にまとめた字幕画面（2つ目の言語を選んだときだけ） */}
          {second && (
            <a
              href={`${captionPageUrl}&lang=both`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost btn-sm"
              style={{ color: 'white', borderColor: '#4b5563' }}
            >
              {t('💬 会議用字幕（2言語）', '💬 Meeting captions (2 languages)')}
            </a>
          )}

          {/* QRコードボタン */}
          <a
            href={`/qr?url=${encodeURIComponent(captionPageUrl)}${second ? `&langs=${target},${second}` : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-sm"
            style={{ color: 'white', borderColor: '#4b5563' }}
          >
            {t('📱 QRコード', '📱 QR code')}
          </a>

          {/* セッション名入力 */}
          <input
            type="text"
            className="input-text"
            placeholder={t('セッション名', 'Session name')}
            value={store.sessionName}
            onChange={(e) => store.setSessionName(e.target.value)}
            style={{ width: '140px', background: '#374151', borderColor: '#4b5563', color: 'white' }}
          />
          <LangSwitch dark />
        </div>
      </header>

      {/* ── ボディ ── */}
      <div className="operator-body">
        {/* ── サイドバー ── */}
        <aside className="operator-sidebar">
          {/* マイク選択 */}
          <MicSelector
            devices={audio.devices}
            selectedDeviceId={audio.selectedDeviceId}
            isCapturing={audio.isCapturing}
            onSelect={(id) => audio.selectDevice(id)}
            onRefresh={() => audio.enumerateDevices()}
          />

          {/* 音量メーター */}
          <AudioMeter
            level={audio.audioLevel}
            isMuted={audio.isMuted}
            isCapturing={audio.isCapturing}
            isSpeaking={store.isSpeaking}
          />

          {/* 訳す先の言語・マイク環境 */}
          <TargetSelect
            target={target}
            onTargetChange={handleTargetChange}
            noise={noise}
            onNoiseChange={handleNoiseChange}
            second={second}
            onSecondChange={handleSecondChange}
          />

          {/* コントロールボタン */}
          <div className="card">
            <div className="card-title">
              {t('操作', 'Controls')}
              <span className="shortcut-hint" style={{ marginLeft: '0.5rem' }}>
                <kbd>Space</kbd> {t('開始/一時停止', 'start/pause')}
              </span>
            </div>

            <div className="btn-group" style={{ flexDirection: 'column' }}>
              {!isStarted ? (
                <button
                  className="btn btn-success btn-xl"
                  onClick={handleStart}
                  disabled={audio.devices.length === 0}
                >
                  {t('▶ 翻訳開始', '▶ Start translating')}
                </button>
              ) : (
                <>
                  <button
                    className={`btn btn-xl ${store.paused ? 'btn-success' : 'btn-warning'}`}
                    onClick={handleTogglePause}
                    title={sessionMode === 'production' ? t('一時停止中も利用時間は減ります', 'Purchased time keeps counting down while paused') : undefined}
                  >
                    {store.paused ? t('▶ 再開', '▶ Resume') : t('⏸ 一時停止', '⏸ Pause')}
                  </button>
                  <button className="btn btn-danger btn-lg" onClick={handleStop}>
                    {t('⏹ 翻訳停止', '⏹ Stop')}
                  </button>
                </>
              )}

              <button
                className="btn btn-ghost btn-sm"
                onClick={handleClear}
                disabled={!isStarted}
              >
                {t('🗑 字幕クリア', '🗑 Clear captions')} <kbd style={{ marginLeft: '0.25rem' }}>C</kbd>
              </button>

              {isStarted && (
                <button
                  className={`btn btn-ghost btn-sm ${audio.isMuted ? 'btn-warning' : ''}`}
                  onClick={() => audio.setMuted(!audio.isMuted)}
                >
                  {audio.isMuted ? t('🔇 ミュート解除', '🔇 Unmute') : t('🔈 ミュート', '🔈 Mute')}
                </button>
              )}
            </div>
          </div>

          {/* 表示設定 */}
          <div className="card">
            <div className="card-title">{t('字幕設定', 'Caption settings')}</div>

            <label style={{ fontSize: '0.8rem', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>
              {t('フォントサイズ', 'Font size')}: {store.fontSize}px
            </label>
            <input
              type="range"
              className="slider"
              min={40}
              max={96}
              value={store.fontSize}
              onChange={(e) => { const v = Number(e.target.value); store.setFontSize(v); ws.sendFontSize(v); }}
            />

            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={store.showOriginal}
                  onChange={(e) => { store.setShowOriginal(e.target.checked); ws.sendShowOriginal(e.target.checked); }}
                />
                {t('原文も表示する', 'Show original text')}
              </label>
            </div>


            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ fontSize: '0.8rem', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>
                {t('OBSスタイル', 'Display style')}
              </label>
              <select
                className="input-text"
                value={store.obsStyle}
                onChange={(e) => { const v = e.target.value as typeof store.obsStyle; store.setOBSStyle(v); ws.sendObsStyle(v); }}
              >
                <option value="transparent-white">{t('透明背景 + 白文字（OBS・スライド用）', 'Transparent + white text (OBS / slides)')}</option>
                <option value="transparent-black">{t('透明背景 + 黒文字（明るい背景用）', 'Transparent + black text (light backgrounds)')}</option>
                <option value="white-black">{t('白背景 + 黒文字（単体モニター表示用）', 'White background + black text (monitor)')}</option>
              </select>
            </div>
          </div>
        </aside>

        {/* ── メインエリア ── */}
        <main className="operator-main">
          {/* 時間警告バナー（grace 前 / grace 中 / 残り5分未満） */}
          {((timeWarning || (remainingSec !== null && remainingSec < 300 && sessionMode === 'production')) && !timeExpired) && (
            <div className="alert" style={{
              background: graceMode ? '#7f1d1d' : '#78350f',
              color: graceMode ? '#fca5a5' : '#fde68a',
              border: `1px solid ${graceMode ? '#dc2626' : '#f59e0b'}`,
              borderRadius: 8,
              padding: '0.75rem 1rem',
              marginBottom: '0.75rem',
              fontSize: '0.9rem',
            }}>
              {graceMode ? '⚠️ ' : '⏰ '}{timeWarning ? trServer(timeWarning) : t(`契約時間まであと${Math.ceil((remainingSec ?? 0) / 60)}分です。`, `${Math.ceil((remainingSec ?? 0) / 60)} minutes of purchased time left.`)}
              {/* 延長ボタン（本番 + session ID あり） */}
              {sessionMode === 'production' && prodSessionId && getActiveEvent()?.managementToken && (
                <button
                  onClick={handleExtension}
                  disabled={extensionLoading || extensionCheckoutOpened}
                  style={{
                    marginLeft: '1rem',
                    background: '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    padding: '0.3rem 0.8rem',
                    fontSize: '0.8rem',
                    cursor: extensionLoading || extensionCheckoutOpened ? 'not-allowed' : 'pointer',
                    opacity: extensionLoading || extensionCheckoutOpened ? 0.7 : 1,
                  }}
                >
                  {extensionLoading ? t('⏳ 処理中...', '⏳ Processing...') : extensionCheckoutOpened ? t('✅ 決済画面を開きました', '✅ Checkout opened') : t(`1時間延長する（${extPriceLabel}）`, `Extend by 1 hour (${extPriceLabel})`)}
                </button>
              )}
            </div>
          )}

          {/* 利用時間終了 */}
          {timeExpired && (
            <div className="alert alert-error" style={{ fontSize: '1rem', padding: '1rem 1.25rem' }}>
              {sessionMode === 'test'
                ? t('⏰ 接続テストの時間が終了しました。', '⏰ The connection test has ended.')
                : t('⏰ 購入済みの利用時間（猶予含む）が終了しました。', '⏰ Your purchased time (including grace period) has ended.')}
              {store.transcriptDirty && store.transcript.length > 0 && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.9rem' }}>{t('翻訳履歴はまだ保存されていません。「翻訳履歴」のTXT/CSVで保存してください。', 'The translation history is not saved yet. Save it with TXT/CSV in the History panel.')}</div>
              )}
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {sessionMode === 'test' ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => { clearSavedTestToken(getActiveEvent()?.id); window.location.reload(); }}
                  >
                    {t('新しい接続テストを始める（1日3回まで）', 'Start a new connection test (up to 3 per day)')}
                  </button>
                ) : (
                <a href="/setup" style={{ color: '#fca5a5', textDecoration: 'underline', fontSize: '0.875rem' }}>
                  {t('セットアップへ戻って追加購入する', 'Back to setup to buy more time')}
                </a>
                )}
              </div>
            </div>
          )}

          {/* エラー・警告表示 */}
          {micWarning && (
            <div className="alert alert-warning">
              🎤 {micWarning}
              <button
                style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => setMicWarning(null)}
              >
                ✕
              </button>
            </div>
          )}

          {store.connectionStatus === 'error' && store.errorMessage && (
            <div className="alert alert-error">
              ⚠️ {t('APIエラー', 'API error')}: {trServer(store.errorMessage)}
            </div>
          )}

          {store.connectionStatus === 'reconnecting' && store.reconnectInfo && (
            <div className="alert alert-warning">
              {t(`🔄 翻訳エンジンに再接続中... (${store.reconnectInfo.attempt}回目 / ${store.reconnectInfo.delay / 1000}秒後に再試行)`, `🔄 Reconnecting to the translation engine... (attempt ${store.reconnectInfo.attempt}, retry in ${store.reconnectInfo.delay / 1000}s)`)}
            </div>
          )}

          {store.connectionStatus === 'connecting' && (
            <div className="alert alert-info">
              {t('🔌 翻訳エンジンに接続中...', '🔌 Connecting to the translation engine...')}
            </div>
          )}

          {isStarted && store.paused && (
            <div className="alert alert-warning">
              {t('⏸ 一時停止中 — 音声の送信を停止しています', '⏸ Paused — audio is not being sent')}
              {sessionMode === 'production' && (
                <div style={{ fontSize: '0.85em', marginTop: '0.25rem' }}>
                  {t('一時停止中も、購入した利用時間は減り続けます。', 'Your purchased time keeps counting down while paused.')}
                </div>
              )}
            </div>
          )}

          {/* 字幕プレビュー */}
          <div className="card">
            <div className="card-title">{t('字幕プレビュー', 'Caption preview')}</div>
            <div className="caption-preview">
              {store.currentCaption || (second && store.currentCaption2) ? (
                <div>
                  {store.showOriginal && store.currentCaption?.original && (
                    <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem', fontStyle: 'italic' }}>
                      {store.currentCaption?.original}
                    </div>
                  )}
                  {store.currentCaption && (
                    <div
                      className={`caption-text-preview ${store.currentCaption.interim ? 'interim' : ''}`}
                    >
                      {store.currentCaption.translation || '...'}
                    </div>
                  )}
                  {second && store.currentCaption2 && (
                    <div
                      className={`caption-text-preview ${store.currentCaption2.interim ? 'interim' : ''}`}
                      style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #374151' }}
                    >
                      {store.currentCaption2.translation || '...'}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#4b5563', fontSize: '1rem' }}>
                  {isStarted ? t('音声を待機中...', 'Waiting for audio...') : t('翻訳開始ボタンを押してください', 'Press Start translating')}
                </div>
              )}
            </div>
          </div>

          {/* セッション終了後のブックマークURL更新通知 */}
          {bookmarkUrl && !isStarted && (
            <div className="card bookmark-update-card">
              <div className="card-title">{t('📌 専用URLを更新してください', '📌 Update your event URL')}</div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-gray-600)', marginBottom: '0.75rem' }}>
                {t('用語集が変わりました。新しいURLでブックマークを上書き保存してください。', 'The glossary changed. Save the new URL as your bookmark.')}
              </p>
              <button
                className="btn btn-primary"
                style={{ width: '100%', marginBottom: '0.5rem' }}
                onClick={() => {
                  navigator.clipboard.writeText(bookmarkUrl).then(() => {
                    setUrlCopied(true);
                    setTimeout(() => setUrlCopied(false), 2000);
                  });
                }}
              >
                {urlCopied ? t('✅ URLをコピーしました', '✅ URL copied') : t('🔗 URLをコピー', '🔗 Copy URL')}
              </button>
              <p className="bookmark-kbd">{t('コピー後 Cmd+D（Mac）/ Ctrl+D（Windows）でブックマーク更新', 'After copying, press Cmd+D (Mac) / Ctrl+D (Windows) to update your bookmark')}</p>
            </div>
          )}

          {/* 用語集 */}
          <GlossaryEditor
            terms={store.glossaryTerms}
            onAdd={store.addGlossaryTerm}
            onRemove={store.removeGlossaryTerm}
            onImport={store.importGlossary}
            onExport={store.exportGlossary}
            onApply={() => ws.sendGlossary(store.glossaryTerms)}
          />

          {/* 翻訳履歴 */}
          <TranscriptHistory
            transcript={store.transcript}
            onExport={store.exportTranscript}
            onClear={store.clearTranscript}
            unsaved={store.transcriptDirty}
          />

          {/* Zoom字幕API（2026-10-05 非表示：Zoom側で1文遅れて表示されるため。戻すときは SHOW_ZOOM_CC を true に） */}
          {SHOW_ZOOM_CC && <div className="card">
            <div className="card-title">{t('📹 Zoom字幕連携', '📹 Zoom captions')}</div>
            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              {t('ZoomミーティングのAPIトークンURLを貼り付けると、翻訳テキストが自動送信されます', 'Paste the Zoom meeting API token URL to send translated captions to Zoom automatically')}
            </div>
            <input
              type="text"
              placeholder="https://wmcc.zoom.us/closedcaption?id=...&token=..."
              value={zoomCaptionUrl}
              onChange={(e) => {
                setZoomCaptionUrl(e.target.value);
                setZoomCaptionEnabled(false);
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: '0.8rem',
                marginBottom: '0.5rem',
                boxSizing: 'border-box',
              }}
            />
            <button
              onClick={() => setZoomCaptionEnabled((v) => !v)}
              disabled={!zoomCaptionUrl.trim()}
              style={{
                background: zoomCaptionEnabled ? '#16a34a' : '#374151',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '0.4rem 1rem',
                fontSize: '0.85rem',
                cursor: zoomCaptionUrl.trim() ? 'pointer' : 'not-allowed',
                opacity: zoomCaptionUrl.trim() ? 1 : 0.5,
              }}
            >
              {zoomCaptionEnabled ? t('✅ 送信中', '✅ Sending') : t('▶ 送信開始', '▶ Start sending')}
            </button>
          </div>}

          {/* キーボードショートカット一覧 */}
          <div className="card">
            <div className="card-title">{t('キーボードショートカット', 'Keyboard shortcuts')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', fontSize: '0.8rem' }}>
              {[
                ['1', t('日本語に訳す', 'Translate to Japanese')],
                ['2', t('Englishに訳す', 'Translate to English')],
                ['Space', t('開始 / 一時停止', 'Start / pause')],
                ['Escape', t('停止', 'Stop')],
                ['C / Del', t('字幕クリア', 'Clear captions')],
              ].map(([key, desc]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <kbd>{key}</kbd>
                  <span style={{ color: '#6b7280' }}>{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
